// Wave 5: the YouTube connection and uploads (api/src/youtube.ts). Kept out of the big Api interface: under the mock
// (VITE_MOCK) the status call fails and the feature simply stays hidden, as it does when the server has no OAuth env.
import { call } from "./api";

export type YoutubeStatus = { enabled: false } | { enabled: true; connected: boolean; connectedAt: string | null; uploadsToday: number; dailyLimit: number; manageUrl: string };

export type YoutubeUpload = {
  id: string;
  version: number | null;
  title: string;
  privacy: "private" | "unlisted" | "public";
  containsSyntheticMedia: boolean;
  status: "uploading" | "done" | "failed";
  progress: number;
  videoId: string | null;
  watchUrl: string | null;
  studioUrl: string | null;
  keptPrivate: boolean;
  error: string | null;
  createdAt: string;
};

export type UploadRequest = { version: number; title: string; description: string; privacy: YoutubeUpload["privacy"]; containsSyntheticMedia: boolean; tags: string[]; confirmGuidelines: true };

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown) => (typeof v === "string" ? v : "");

export function normStatus(v: unknown): YoutubeStatus {
  if (!isObj(v) || v.enabled !== true) return { enabled: false };
  return { enabled: true, connected: v.connected === true, connectedAt: str(v.connectedAt) || null, uploadsToday: Number(v.uploadsToday) || 0, dailyLimit: Number(v.dailyLimit) || 0, manageUrl: str(v.manageUrl) || "https://myaccount.google.com/connections" };
}

export function normUpload(v: unknown): YoutubeUpload | null {
  if (!isObj(v) || !str(v.id)) return null;
  const status = (["uploading", "done", "failed"].includes(str(v.status)) ? str(v.status) : "failed") as YoutubeUpload["status"];
  const privacy = (["private", "unlisted", "public"].includes(str(v.privacy)) ? str(v.privacy) : "private") as YoutubeUpload["privacy"];
  return {
    id: str(v.id),
    version: typeof v.version === "number" ? v.version : null,
    title: str(v.title),
    privacy,
    containsSyntheticMedia: v.containsSyntheticMedia === true,
    status,
    progress: Math.max(0, Math.min(1, Number(v.progress) || 0)),
    videoId: str(v.videoId) || null,
    watchUrl: str(v.watchUrl) || null,
    studioUrl: str(v.studioUrl) || null,
    keptPrivate: v.keptPrivate === true,
    error: str(v.error) || null,
    createdAt: str(v.createdAt),
  };
}

export const youtube = {
  async status(): Promise<YoutubeStatus> {
    return normStatus(await call("GET", "youtube").catch(() => null));
  },
  /** Google's consent page; the caller sends the browser there. */
  async connectUrl(returnTo: string): Promise<string> {
    const r = await call("POST", "youtube/connect", { returnTo });
    return isObj(r) ? str(r.url) : "";
  },
  async disconnect(): Promise<void> {
    await call("POST", "youtube/disconnect", {});
  },
  async upload(projectId: string, body: UploadRequest): Promise<YoutubeUpload | null> {
    const r = await call("POST", `projects/${encodeURIComponent(projectId)}/youtube/upload`, body);
    return normUpload(isObj(r) ? r.upload : null);
  },
  async uploads(projectId: string): Promise<YoutubeUpload[]> {
    const r = await call("GET", `projects/${encodeURIComponent(projectId)}/youtube/uploads`);
    return (isObj(r) && Array.isArray(r.uploads) ? r.uploads : []).map(normUpload).filter((u): u is YoutubeUpload => !!u);
  },
};

/** The ?youtube= outcome the callback redirects back with: the toast to show (null: none). */
export function callbackMessage(search: string): { text: string; tone: "good" | "bad" } | null {
  const v = new URLSearchParams(search).get("youtube");
  if (v === "connected") return { text: "YouTube is connected. You can upload from a video's Publish fold.", tone: "good" };
  if (v === "cancelled") return { text: "YouTube wasn't connected.", tone: "bad" };
  if (v === "denied") return { text: "YouTube needs the upload permission to post your videos. Try again and leave it ticked.", tone: "bad" };
  if (v === "error") return { text: "Couldn't connect YouTube. Try again.", tone: "bad" };
  return null;
}
